// src/context/RoddoRadioProvider.tsx
import React from 'react';
import {
  roddoRadioTracks,
  type RoddoRadioTrack,
} from '../data/roddoRadioTracks';

interface SelectTrackOptions {
  play?: boolean;
}

interface RoddoRadioContextValue {
  tracks: RoddoRadioTrack[];
  currentTrack: RoddoRadioTrack;
  currentTrackId: string;
  isPlaying: boolean;
  isMuted: boolean;
  volume: number;
  autoplayEnabled: boolean;
  duration: number;
  currentTime: number;
  progress: number;
  playError: string;
  play: () => Promise<void>;
  pause: () => void;
  togglePlay: () => void;
  nextTrack: () => void;
  previousTrack: () => void;
  selectTrack: (track: RoddoRadioTrack, options?: SelectTrackOptions) => void;
  setVolume: (value: number) => void;
  setAutoplayEnabled: (enabled: boolean) => void;
  toggleMute: () => void;
  seekTo: (time: number) => void;
}

const RoddoRadioContext = React.createContext<RoddoRadioContextValue | null>(null);

const STORAGE_KEYS = {
  volume: 'roddoRadio.volume',
  muted: 'roddoRadio.muted',
  autoplay: 'roddoRadio.autoplayEnabled',
  trackId: 'roddoRadio.currentTrackId',
  settingsVersion: 'roddoRadio.settingsVersion',
} as const;

const DEFAULT_VOLUME = 0.3;
const SETTINGS_VERSION = 'v3-random-autoplay-30';

const clamp01 = (value: number) => {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(1, value));
};

const readStorage = (key: string) => {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeStorage = (key: string, value: string) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Storage can be unavailable in private mode; radio should still work.
  }
};

const readStoredVolume = () => {
  const version = readStorage(STORAGE_KEYS.settingsVersion);
  if (version !== SETTINGS_VERSION) return DEFAULT_VOLUME;

  const raw = readStorage(STORAGE_KEYS.volume);
  const parsed = raw === null ? NaN : Number(raw);
  return Number.isFinite(parsed) ? clamp01(parsed) : DEFAULT_VOLUME;
};

const readStoredBoolean = (key: string, fallback = false) => {
  const version = readStorage(STORAGE_KEYS.settingsVersion);
  if (version !== SETTINGS_VERSION) return fallback;

  const raw = readStorage(key);
  if (raw === 'true') return true;
  if (raw === 'false') return false;
  return fallback;
};

const pickRandomTrackId = () => {
  if (!roddoRadioTracks.length) return '';
  const index = Math.floor(Math.random() * roddoRadioTracks.length);
  return roddoRadioTracks[index]?.id || roddoRadioTracks[0]?.id || '';
};

const readStoredTrackId = () => {
  // Roddo Radio should feel fresh on each app start.
  // We still write the active track to storage for settings/UI continuity during a session,
  // but new app loads begin from a random Daniel track.
  return pickRandomTrackId();
};

export const RoddoRadioProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const audioRef = React.useRef<HTMLAudioElement | null>(null);
  const pendingPlayRef = React.useRef(false);
  const autoplayAttemptedRef = React.useRef(false);
  const pendingUserGesturePlayRef = React.useRef(false);

  const [currentTrackId, setCurrentTrackId] = React.useState(readStoredTrackId);
  const [isPlaying, setIsPlaying] = React.useState(false);
  const [isMuted, setIsMuted] = React.useState(() =>
    readStoredBoolean(STORAGE_KEYS.muted, false),
  );
  const [volumeState, setVolumeState] = React.useState(readStoredVolume);
  const [autoplayEnabledState, setAutoplayEnabledState] = React.useState(() =>
    readStoredBoolean(STORAGE_KEYS.autoplay, true),
  );
  const [duration, setDuration] = React.useState(0);
  const [currentTime, setCurrentTime] = React.useState(0);
  const [playError, setPlayError] = React.useState('');

  const currentTrack = React.useMemo(() => {
    return (
      roddoRadioTracks.find((track) => track.id === currentTrackId) ||
      roddoRadioTracks[0]
    );
  }, [currentTrackId]);

  const currentIndex = React.useMemo(() => {
    const index = roddoRadioTracks.findIndex((track) => track.id === currentTrackId);
    return index >= 0 ? index : 0;
  }, [currentTrackId]);

  const progress = duration > 0 ? Math.min(100, (currentTime / duration) * 100) : 0;

  const play = React.useCallback(async () => {
    const audio = audioRef.current;
    if (!audio || !currentTrack) return;

    setPlayError('');
    try {
      if (audio.src !== currentTrack.src) {
        audio.src = currentTrack.src;
        audio.load();
      }
      await audio.play();
      pendingUserGesturePlayRef.current = false;
      setIsPlaying(true);
    } catch (error) {
      console.error('[RoddoRadio] play failed', error);
      pendingUserGesturePlayRef.current = autoplayEnabledState;
      setIsPlaying(false);
      setPlayError('Tap anywhere once if your device blocks first-load audio.');
    }
  }, [autoplayEnabledState, currentTrack]);

  const pause = React.useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.pause();
    setIsPlaying(false);
  }, []);

  const selectTrack = React.useCallback(
    (track: RoddoRadioTrack, options?: SelectTrackOptions) => {
      if (!track) return;
      pendingPlayRef.current = Boolean(options?.play ?? isPlaying);
      setPlayError('');
      setCurrentTime(0);
      setDuration(0);
      setCurrentTrackId(track.id);
    },
    [isPlaying],
  );

  const moveTrack = React.useCallback(
    (direction: 1 | -1) => {
      if (!roddoRadioTracks.length) return;
      const nextIndex =
        (currentIndex + direction + roddoRadioTracks.length) % roddoRadioTracks.length;
      selectTrack(roddoRadioTracks[nextIndex], { play: isPlaying });
    },
    [currentIndex, isPlaying, selectTrack],
  );

  const nextTrack = React.useCallback(() => moveTrack(1), [moveTrack]);
  const previousTrack = React.useCallback(() => moveTrack(-1), [moveTrack]);

  const togglePlay = React.useCallback(() => {
    if (isPlaying) {
      pause();
      return;
    }
    void play();
  }, [isPlaying, pause, play]);

  const setVolume = React.useCallback((value: number) => {
    const next = clamp01(value);
    setVolumeState(next);
    setIsMuted(next <= 0);
  }, []);

  const setAutoplayEnabled = React.useCallback((enabled: boolean) => {
    setAutoplayEnabledState(enabled);
    if (enabled) pendingPlayRef.current = true;
  }, []);

  const toggleMute = React.useCallback(() => {
    setIsMuted((prev) => {
      if (prev && volumeState <= 0) setVolumeState(DEFAULT_VOLUME);
      return !prev;
    });
  }, [volumeState]);

  const seekTo = React.useCallback(
    (time: number) => {
      const audio = audioRef.current;
      if (!audio || !Number.isFinite(time)) return;
      const next = Math.max(0, Math.min(duration || audio.duration || 0, time));
      audio.currentTime = next;
      setCurrentTime(next);
    },
    [duration],
  );

  React.useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !currentTrack) return;

    if (audio.src !== currentTrack.src) {
      audio.pause();
      audio.src = currentTrack.src;
      audio.currentTime = 0;
      audio.load();
    }

    if (pendingPlayRef.current) {
      pendingPlayRef.current = false;
      window.setTimeout(() => {
        audio
          .play()
          .then(() => {
            pendingUserGesturePlayRef.current = false;
            setIsPlaying(true);
          })
          .catch((error) => {
            console.error('[RoddoRadio] play failed', error);
            pendingUserGesturePlayRef.current = autoplayEnabledState;
            setIsPlaying(false);
            setPlayError('Tap anywhere once if your device blocks first-load audio.');
          });
      }, 0);
    } else {
      setIsPlaying(false);
    }
  }, [autoplayEnabledState, currentTrack]);

  React.useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.volume = volumeState;
    audio.muted = isMuted;
  }, [isMuted, volumeState]);

  React.useEffect(() => {
    writeStorage(STORAGE_KEYS.settingsVersion, SETTINGS_VERSION);
    writeStorage(STORAGE_KEYS.volume, String(volumeState));
  }, [volumeState]);

  React.useEffect(() => {
    writeStorage(STORAGE_KEYS.muted, String(isMuted));
  }, [isMuted]);

  React.useEffect(() => {
    writeStorage(STORAGE_KEYS.autoplay, String(autoplayEnabledState));
  }, [autoplayEnabledState]);

  React.useEffect(() => {
    if (currentTrackId) writeStorage(STORAGE_KEYS.trackId, currentTrackId);
  }, [currentTrackId]);

  React.useEffect(() => {
    if (!autoplayEnabledState || autoplayAttemptedRef.current) return;
    autoplayAttemptedRef.current = true;
    pendingPlayRef.current = false;
    void play();
  }, [autoplayEnabledState, play]);

  React.useEffect(() => {
    const pauseForBackground = () => {
      const audio = audioRef.current;
      pendingPlayRef.current = false;
      pendingUserGesturePlayRef.current = false;
      if (audio && !audio.paused) {
        audio.pause();
      }
      setIsPlaying(false);
    };

    const handleVisibilityChange = () => {
      if (document.hidden) pauseForBackground();
    };

    window.addEventListener('pagehide', pauseForBackground);
    window.addEventListener('blur', pauseForBackground);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('pagehide', pauseForBackground);
      window.removeEventListener('blur', pauseForBackground);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  React.useEffect(() => {
    if (!autoplayEnabledState) {
      pendingUserGesturePlayRef.current = false;
      return;
    }

    const resumeAfterGesture = () => {
      if (!pendingUserGesturePlayRef.current) return;
      pendingUserGesturePlayRef.current = false;
      void play();
    };

    window.addEventListener('pointerdown', resumeAfterGesture, { passive: true });
    window.addEventListener('touchstart', resumeAfterGesture, { passive: true });
    window.addEventListener('keydown', resumeAfterGesture);

    return () => {
      window.removeEventListener('pointerdown', resumeAfterGesture);
      window.removeEventListener('touchstart', resumeAfterGesture);
      window.removeEventListener('keydown', resumeAfterGesture);
    };
  }, [autoplayEnabledState, play]);

  React.useEffect(() => {
    return () => {
      const audio = audioRef.current;
      if (audio) audio.pause();
    };
  }, []);

  const value = React.useMemo<RoddoRadioContextValue>(
    () => ({
      tracks: roddoRadioTracks,
      currentTrack,
      currentTrackId,
      isPlaying,
      isMuted,
      volume: volumeState,
      autoplayEnabled: autoplayEnabledState,
      duration,
      currentTime,
      progress,
      playError,
      play,
      pause,
      togglePlay,
      nextTrack,
      previousTrack,
      selectTrack,
      setVolume,
      setAutoplayEnabled,
      toggleMute,
      seekTo,
    }),
    [
      currentTrack,
      currentTrackId,
      isPlaying,
      isMuted,
      volumeState,
      autoplayEnabledState,
      duration,
      currentTime,
      progress,
      playError,
      play,
      pause,
      togglePlay,
      nextTrack,
      previousTrack,
      selectTrack,
      setVolume,
      setAutoplayEnabled,
      toggleMute,
      seekTo,
    ],
  );

  return (
    <RoddoRadioContext.Provider value={value}>
      <audio
        ref={audioRef}
        src={currentTrack?.src}
        preload="auto"
        onLoadedMetadata={(event) => {
          const audio = event.currentTarget;
          setDuration(Number.isFinite(audio.duration) ? audio.duration : 0);
        }}
        onDurationChange={(event) => {
          const audio = event.currentTarget;
          setDuration(Number.isFinite(audio.duration) ? audio.duration : 0);
        }}
        onTimeUpdate={(event) => setCurrentTime(event.currentTarget.currentTime || 0)}
        onPause={() => setIsPlaying(false)}
        onPlay={() => setIsPlaying(true)}
        onVolumeChange={(event) => {
          const audio = event.currentTarget;
          setVolumeState(audio.volume);
          setIsMuted(audio.muted || audio.volume <= 0);
        }}
        onEnded={() => {
          pendingPlayRef.current = true;
          const nextIndex =
            (currentIndex + 1 + roddoRadioTracks.length) % roddoRadioTracks.length;
          setCurrentTrackId(roddoRadioTracks[nextIndex]?.id || currentTrackId);
        }}
      />
      {children}
    </RoddoRadioContext.Provider>
  );
};

export const useRoddoRadio = () => {
  const value = React.useContext(RoddoRadioContext);
  if (!value) {
    throw new Error('useRoddoRadio must be used inside RoddoRadioProvider');
  }
  return value;
};
